import { avatarToneClassName, companyAvatarSeed, companyInitials } from "@/lib/avatar"
import { isAppCompany } from "@/lib/brand"
import { cn } from "@/lib/utils"

export type CompanyLogoSize = 24 | 32 | 36 | 44 | 64

export type CompanyLogoProps = {
  company: { id: number; name: string; slug?: string | null }
  size?: CompanyLogoSize
  pending?: boolean
  dot?: "accent" | "amber" | "danger" | null
  /** Всегда монограмма «fr», независимо от компании (шапка сайдбара, экраны входа). */
  brand?: boolean
  className?: string
}

const SIZE_CLASS: Record<CompanyLogoSize, string> = {
  24: "size-6 rounded-lg text-[10px]",
  32: "size-8 rounded-lg text-xs",
  36: "size-9 rounded-lg text-[13px]",
  44: "size-11 rounded-lg text-[15px]",
  64: "size-16 rounded-lg text-[22px]",
}

// Монограмма бренда: Gabarito 800, крупнее инициалов — буквы строчные и визуально мельче.
const BRAND_MARK_TEXT_CLASS: Record<CompanyLogoSize, string> = {
  24: "text-[15px]",
  32: "text-[20px]",
  36: "text-[23px]",
  44: "text-[28px]",
  64: "text-[41px]",
}

export function CompanyLogo({
  company,
  size = 36,
  pending = false,
  dot = null,
  brand = false,
  className,
}: CompanyLogoProps) {
  const brandMark = !pending && (brand || isAppCompany(company))
  return (
    <span aria-hidden className={cn("relative inline-flex shrink-0", className)}>
      {brandMark ? (
        <span
          className={cn(
            "inline-flex items-center justify-center border border-[var(--ui-brand-mark-border)] bg-[var(--ui-brand-mark-bg)] leading-none font-extrabold tracking-[-0.04em] text-[var(--ui-brand-mark-fg)] select-none",
            SIZE_CLASS[size],
            BRAND_MARK_TEXT_CLASS[size],
          )}
          style={{ fontFamily: "var(--font-display)" }}
        >
          f<span className="text-[var(--ui-brand-mark-accent)]">r</span>
        </span>
      ) : (
        <span
          className={cn(
            "inline-flex items-center justify-center leading-none font-semibold tracking-tight select-none",
            SIZE_CLASS[size],
            pending
              ? "border border-dashed border-ui-border-strong bg-transparent text-ui-text-muted"
              : avatarToneClassName(companyAvatarSeed(company.id)),
          )}
        >
          {companyInitials(company.name)}
        </span>
      )}
      {dot ? (
        <span
          className={cn(
            "absolute -top-0.5 -right-0.5 size-2 rounded-full ring-2 ring-ui-bg-elevated",
            dot === "accent" ? "bg-ui-accent" : dot === "danger" ? "bg-ui-danger" : "bg-[var(--ui-role-owner-dot)]",
          )}
        />
      ) : null}
    </span>
  )
}
